import {
  AVATAR_FRAME_INSET,
  LAST_CALL_FRAME_ID,
  LEAGUE_FRAME_IDS,
  avatarFrameById,
  type AvatarFrameDef,
} from '@shared/index'

/** Painted rings. Each one is a square PNG with a round hole of radius 0.30. */
const IMAGE_FRAME_SRC = new Map<string, string>([
  [LAST_CALL_FRAME_ID, '/assets/avatars/frames/last-call.png?v=2'],
  ...LEAGUE_FRAME_IDS.map(
    (id) => [id, `/assets/avatars/frames/${id}.png`] as [string, string],
  ),
])

/**
 * Portrait radius as a fraction of the canvas. A hair past the punched
 * hole (0.30) so the photo tucks under the inner scales with no gap.
 */
export const IMAGE_FRAME_PORTRAIT = 0.312

const frameImages = new Map<string, HTMLImageElement>()
const frameLoads = new Map<string, Promise<HTMLImageElement>>()

export function isImageFrame(frameId: string): boolean {
  return IMAGE_FRAME_SRC.has(frameId)
}

export function loadImageFrame(frameId: string): Promise<HTMLImageElement> {
  const ready = frameImages.get(frameId)
  if (ready) return Promise.resolve(ready)
  const src = IMAGE_FRAME_SRC.get(frameId)
  if (!src) return Promise.reject(new Error(`not an image frame: ${frameId}`))
  let pending = frameLoads.get(frameId)
  if (!pending) {
    const img = new Image()
    pending = new Promise((resolve, reject) => {
      img.onload = () => {
        frameImages.set(frameId, img)
        resolve(img)
      }
      img.onerror = () => {
        frameLoads.delete(frameId)
        reject(new Error(`${frameId} frame missing`))
      }
    })
    frameLoads.set(frameId, pending)
    img.src = src
  }
  return pending
}

function drawImageFrame(
  ctx: CanvasRenderingContext2D,
  size: number,
  frameId: string,
): void {
  const img = frameImages.get(frameId)
  if (!img) return
  ctx.drawImage(img, 0, 0, size, size)
}

function addRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2)
  ctx.moveTo(x + rr, y)
  ctx.arcTo(x + w, y, x + w, y + h, rr)
  ctx.arcTo(x + w, y + h, x, y + h, rr)
  ctx.arcTo(x, y + h, x, y, rr)
  ctx.arcTo(x, y, x + w, y, rr)
  ctx.closePath()
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath()
  addRoundRect(ctx, x, y, w, h, r)
}

function frameInset(size: number): number {
  return Math.max(2, Math.round(size * AVATAR_FRAME_INSET))
}

/**
 * Square beveled frame. Call after the inner art is painted.
 */
export function drawSquareFrame(
  ctx: CanvasRenderingContext2D,
  size: number,
  frameId: string,
): void {
  const def = avatarFrameById(frameId)
  if (isImageFrame(frameId)) {
    drawImageFrame(ctx, size, frameId)
    return
  }
  const inset = frameInset(size)
  const radius = Math.max(2, size * 0.045)
  const innerR = Math.max(1, radius * 0.4)
  const inner = size - inset * 2
  const grad = ctx.createLinearGradient(0, 0, size, size)
  grad.addColorStop(0, def.light)
  grad.addColorStop(0.42, def.metal)
  grad.addColorStop(1, def.dark)

  ctx.save()
  ctx.beginPath()
  addRoundRect(ctx, 0, 0, size, size, radius)
  addRoundRect(ctx, inset, inset, inner, inner, innerR)
  ctx.fillStyle = grad
  ctx.fill('evenodd')
  ctx.restore()

  ctx.strokeStyle = def.ink
  ctx.lineWidth = Math.max(1, size * 0.012)
  roundRect(ctx, inset, inset, inner, inner, innerR)
  ctx.stroke()

  ctx.strokeStyle = def.light
  ctx.lineWidth = Math.max(1, size * 0.01)
  roundRect(ctx, 1.5, 1.5, size - 3, size - 3, radius)
  ctx.stroke()

  if (size >= 40) drawGems(ctx, size, inset, def)
}

/**
 * Circular metal ring for chat / Life Point chips.
 */
export function drawCircleFrame(
  ctx: CanvasRenderingContext2D,
  size: number,
  frameId: string,
): void {
  const def = avatarFrameById(frameId)
  if (isImageFrame(frameId)) {
    drawImageFrame(ctx, size, frameId)
    return
  }
  const inset = frameInset(size)
  const cx = size / 2
  const cy = size / 2
  const outer = size / 2 - 0.5
  const inner = Math.max(1, size / 2 - inset)
  const grad = ctx.createLinearGradient(0, 0, size, size)
  grad.addColorStop(0, def.light)
  grad.addColorStop(0.42, def.metal)
  grad.addColorStop(1, def.dark)

  ctx.save()
  ctx.beginPath()
  ctx.arc(cx, cy, outer, 0, Math.PI * 2)
  ctx.arc(cx, cy, inner, 0, Math.PI * 2, true)
  ctx.fillStyle = grad
  ctx.fill()
  ctx.restore()

  ctx.beginPath()
  ctx.arc(cx, cy, inner, 0, Math.PI * 2)
  ctx.strokeStyle = def.ink
  ctx.lineWidth = Math.max(1, size * 0.03)
  ctx.stroke()

  ctx.beginPath()
  ctx.arc(cx, cy, outer - 0.6, 0, Math.PI * 2)
  ctx.strokeStyle = def.light
  ctx.lineWidth = Math.max(1, size * 0.02)
  ctx.stroke()
}

function drawGems(
  ctx: CanvasRenderingContext2D,
  size: number,
  inset: number,
  def: AvatarFrameDef,
): void {
  const gem = Math.max(3, size * 0.05)
  const pad = Math.max(2, inset * 0.28)
  const spots: [number, number][] = [
    [pad, pad],
    [size - pad - gem, pad],
    [pad, size - pad - gem],
    [size - pad - gem, size - pad - gem],
  ]
  for (const [x, y] of spots) {
    ctx.fillStyle = def.accent
    ctx.fillRect(x, y, gem, gem)
    ctx.strokeStyle = def.light
    ctx.lineWidth = 1
    ctx.strokeRect(x + 0.5, y + 0.5, gem - 1, gem - 1)
  }
}

export function avatarArtInset(size: number, _frameId?: string): number {
  return frameInset(size)
}
